import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

export interface SignaturePadHandle {
  clear: () => void;
  getTrimmedDataURL: (type?: string, quality?: number) => string;
  isEmpty: () => boolean;
}

export interface SignaturePadProps {
  penColor?: string;
  width?: number;
  height?: number;
  backgroundColor?: string;
  onEnd?: () => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ penColor = 'black', width = 500, height = 175, backgroundColor = '#fff', onEnd }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const sigRef = useRef<SignatureCanvas>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
      const updateDimensions = () => {
        if (containerRef.current) {
          const { offsetWidth } = containerRef.current;
          const newHeight = isMobile ? 150 : height;
          setDimensions({
            width: offsetWidth,
            height: newHeight
          });

          // Force canvas resize
          if (sigRef.current) {
            const canvas = sigRef.current as any;
            canvas.width = offsetWidth;
            canvas.height = newHeight;
            canvas.clear();
          }
        }
      };

      updateDimensions();
      window.addEventListener('resize', updateDimensions);

      return () => {
        window.removeEventListener('resize', updateDimensions);
      };
    }, [isMobile, height]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      clear: () => sigRef.current?.clear() ?? undefined,
      getTrimmedDataURL: (type, q) => sigRef.current?.getTrimmedCanvas().toDataURL(type, q) ?? '',
      isEmpty: () => sigRef.current?.isEmpty() ?? true
    }));

    return (
      <Box
        ref={containerRef}
        sx={{
          border: '1px solid rgba(0,0,0,0.2)',
          bgcolor: backgroundColor,
          width: '100%',
          height: isMobile ? 150 : height,
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        {dimensions.width > 0 && (
          <SignatureCanvas
            ref={sigRef}
            penColor={penColor}
            onEnd={onEnd}
            canvasProps={{
              width: dimensions.width,
              height: dimensions.height,
              className: 'signature-canvas'
            }}
          />
        )}
      </Box>
    );
  }
);
